/**
 * Browser Block Screen - lo que ve un jugador que entra al área de participante
 * desde el navegador del celular en vez de la app.
 *
 * @module components/BrowserBlockScreen
 * @version 6.11.43.00
 *
 * v6.11.43.00 - Deja de ser un cartel de "Instalación Requerida" y pasa a ser
 *               una bienvenida: "¡Ya estás dentro de La Liga!" + el único paso
 *               que falta. Las instrucciones viven ahora en
 *               `components/install/InstallGuidePanel` (compartidas con
 *               `PWAInstallPrompt`) y son las del navegador real del jugador —
 *               Samsung Internet y Safari incluidos, que son justo donde el
 *               botón de instalar no puede funcionar.
 * v6.11.41.00 - Nuevo motivo `push`: el usuario llegó tocando una notificación
 *               (o sea, tiene la app instalada sí o sí) y el navegador se comió
 *               el deep-link. Mostrarle "instalá la app" ahí es desorientador.
 * v4.10.2 - Updated visual style to match system design (PWAInstallPrompt pattern)
 */

import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { InstallGuidePanel } from './install/InstallGuidePanel';

interface BrowserBlockScreenProps {
  /** Nombre del jugador, para saludarlo por su nombre. */
  userName?: string | null;
  /**
   * Motivo del bloqueo.
   * - `active_league`: el jugador YA participa de una edición activa (y muy
   *   probablemente ya tiene la app instalada de una edición previa): el copy
   *   prioriza "abrí la app que ya tenés" en vez de "instalá la app".
   * - `push`: llegó tocando una notificación push. Las push solo existen dentro
   *   de la app instalada, así que la tiene sí o sí; lo único que falló es que
   *   el navegador se quedó con el deep-link.
   */
  reason?: 'default' | 'active_league' | 'push';
}

export const BrowserBlockScreen: React.FC<BrowserBlockScreenProps> = ({
  userName,
  reason = 'default'
}) => {
  const isFromPush = reason === 'push';
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    const timer = setTimeout(() => setIsAnimating(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="browser-block-title"
        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 my-auto relative transform transition-all duration-300 ${
          isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {isFromPush ? (
          /* ------------------------------------------------------------- */
          /* Vino de una push: tiene la app. Solo hay que mandarlo ahí.     */
          /* ------------------------------------------------------------- */
          <>
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-500/30">
                <Bell size={40} className="text-white" />
              </div>
            </div>

            <h2
              id="browser-block-title"
              className="text-2xl font-display font-black uppercase tracking-tight text-text-main text-center mb-2"
            >
              Abrí la notificación en la app
            </h2>

            <p className="text-text-muted text-center mb-6 leading-snug">
              Tocaste una notificación de Liga Ajupam y se abrió el navegador. Abrí la app
              desde tu pantalla de inicio y vas a encontrar la novedad ahí.
            </p>

            <div className="p-4 bg-success-bg border border-success/20 rounded-xl">
              <p className="text-sm text-green-800 text-center leading-snug">
                <strong>Cerrá esta pestaña</strong>
                <br />
                y abrí <strong>Liga Ajupam</strong> desde tu pantalla de inicio.
              </p>
            </div>
          </>
        ) : (
          <div id="browser-block-title">
            <InstallGuidePanel
              userName={userName}
              variant={reason === 'active_league' ? 'returning' : 'welcome'}
            />
          </div>
        )}

        {/* Nota al pie: por qué el navegador no alcanza */}
        <p className="text-xs text-text-muted text-center mt-4 leading-snug">
          El área de jugadores funciona solo desde la app. La inscripción y la
          información pública siguen disponibles en el navegador.
        </p>
      </div>
    </div>
  );
};

export default BrowserBlockScreen;
